import { Box, Button, Grid } from '@mui/material';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { InferType } from 'yup';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { AvailablePaymentMethodsEnum, hasFlag } from 'src/shared/models/enums/availablePaymentMethods.enum';
import { AssociationAccountTypeEnum, getAssociationAccountTypeDescription } from 'src/shared/models/enums/associationAccountType.enum';
import { InvoiceGroup } from 'src/shared/models/invoiceGroup/invoiceGroup';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getInvoiceGroupById, updateInvoiceGroup } from 'src/shared/services/invoiceGroupService';
import { getEInvoiceSender } from 'src/shared/services/electronicInvoiceSenderService';
import { useSchemaInvoiceGroup } from '../schemas/useSchemaInvoiceGroup';
import { PaymentMethodsConfiguration } from './paymentMethodsConfiguration';
import { ActivePaymentOptionsInfo } from './activePaymentOptionsInfo';

export const AdminInvoiceGroupPaymentMethodsTab = () => {
  const params = useParams();
  const { t: tLocal } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [eInvoiceSenderExists, setEInvoiceSenderExists] = useState(false);
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();
  const isSuperAdmin = getPermission(currentUser, PermissionEnum.InvoiceCreateForCustomer);
  const [invoiceGroup, setInvoiceGroup] = useState<InvoiceGroup>();

  const invoiceGroupSchema = useSchemaInvoiceGroup();
  type InvoiceGroupInterface = InferType<typeof invoiceGroupSchema>;

  const formMethods = useForm<InvoiceGroupInterface>({
    resolver: yupResolver(invoiceGroupSchema),
    mode: 'all',
    defaultValues: {
      swishAccount: '',
      swishData1: '',
      swishActive: '0',

      // Stripe
      stripeAccount: '',
      stripeActive: '0',

      // Bank
      accountNr: '',
      accountType: -1,
      accountName: '',
      iban: '',
      bic: '',

      // Autogiro
      autogiroCustomerNr: '',
      autogiroAccount: '',

      // Övriga betalmetodinställningar
      paymentMethod: 0,
      paymentMethodOrder: '',
      pmSelectionHeader: '',
      preSelect: false,
      autoApprovePayments: false,
      sharedAccount: false,

      // Kortdata
      cardData1: '',
      cardData2: '',
      cardData3: '',
      cardSupplier: 0,

      // Avgifter / avtal
      adminFee: 0,

      // E-faktura
      electronicInvoiceEmailNotification: '',

      // Övriga metadata
      customerNr: '',
      name: '',
      owner_id: 0,
      localAssIds: '',
      payerNumberIsBirthdate: false,
      continueBtnText: '',
      senderTitle: '',
      template_id: 0,
      paymentMethods: [],
    },
  });
  const { handleSubmit, watch, setValue, reset } = formMethods;

  function associationAccountTypeDescription(type: AssociationAccountTypeEnum): string {
    return getAssociationAccountTypeDescription(type, t);
  }

  const paymentMethod = watch('paymentMethod');
  const isSwishActive = hasFlag(paymentMethod, AvailablePaymentMethodsEnum.Swish);
  const isStripeActive = hasFlag(paymentMethod, AvailablePaymentMethodsEnum.Card);

  useEffect(() => {
    const accountType = watch('accountType') || AvailablePaymentMethodsEnum.NotSet;
    const accountName = associationAccountTypeDescription(accountType);
    const setStripeStatus = isStripeActive ? '1' : '0';
    const setSwishStatus = isSwishActive ? '1' : '0';

    if (watch('stripeActive') !== setStripeStatus) {
      setValue('stripeActive', setStripeStatus);
    }
    if (watch('swishActive') !== setSwishStatus) {
      setValue('swishActive', setSwishStatus);
    }
    if (watch('accountName') !== accountName) {
      setValue('accountName', accountName);
    }
  }, [isStripeActive, isSwishActive, setValue, watch]);

  const fetchEInvoiceSenderStatus = useCallback(
    (ownerId: number) => {
      getEInvoiceSender(ownerId)
        .then((res) => {
          if (isMounted()) setEInvoiceSenderExists(!!res.data);
        })
        .catch(() => {
          if (isMounted()) setEInvoiceSenderExists(false);
        });
    },
    [isMounted],
  );

  const fetchInvoiceGroup = useCallback(
    async (id: number, isRefetch = false) => {
      if (!isRefetch) setLoading(true);
      try {
        const result = await getInvoiceGroupById(id);
        if (!isMounted()) return;

        const paymentMethodsSorted = [...(result.data.paymentMethods ?? [])].sort((a, b) => (a.pos ?? 0) - (b.pos ?? 0));
        const normalized = {
          ...result.data,
          paymentMethods: paymentMethodsSorted,
          accountName: '',
          autogiroAccount: result.data.autogiroAccount ?? '',
        };

        setInvoiceGroup(normalized);
        reset(normalized);
        fetchEInvoiceSenderStatus(result.data.owner_id);
      } catch (err) {
        if (isMounted()) ConsoleHelper.log(err);
      } finally {
        if (isMounted() && !isRefetch) setLoading(false);
      }
    },
    [isMounted, reset, fetchEInvoiceSenderStatus],
  );

  useEffect(() => {
    if (!params.id) return;
    fetchInvoiceGroup(+params.id);
  }, [params.id, fetchInvoiceGroup]);

  const refetchInvoiceGroup = useCallback(() => {
    if (!params.id) return;
    fetchInvoiceGroup(Number(params.id), true);
  }, [params.id, fetchInvoiceGroup]);

  const onSubmit = useCallback(
    async (data: InvoiceGroupInterface) => {
      if (!invoiceGroup) return;
      setSaving(true);
      try {
        // Schemat använder paymentMethodId men runtime-datat (från InvoiceGroup) har type
        const methods = (data.paymentMethods ?? []) as unknown as { type: number; pos: number; active: '0' | '1' }[];

        // Bygg ihop bitmask från aktiva betalmetoder
        // eslint-disable-next-line no-bitwise
        const paymentMethodBitmask = methods.reduce((acc, m) => (m.active === '1' ? acc | m.type : acc), 0);

        // Bygg paymentMethodOrder: "type,pos|type,pos|..." baserat på arrayens ordning (index = visuell ordning)
        const paymentMethodOrder = methods.map((m, index) => `${m.type},${index + 1}`).join('|');

        const dataToPost: InvoiceGroup = {
          ...invoiceGroup,
          paymentMethod: paymentMethodBitmask,
          paymentMethodOrder,
          preSelect: !!data.preSelect,
        };

        await updateInvoiceGroup(dataToPost);

        if (isMounted()) {
          toast.success(tLocal('invoice:adminInvoiceGroupDetailPage.saveSuccess') as string);
          refetchInvoiceGroup();
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('error', e);
      } finally {
        if (isMounted()) setSaving(false);
      }
    },
    [isMounted, invoiceGroup, tLocal, refetchInvoiceGroup],
  );

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <LoadingOverlay loading={loading} />
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {!loading && invoiceGroup && (
              <Grid container spacing={5}>
                <Grid item xs={12} sm={12} md={6} lg={7}>
                  <PaymentMethodsConfiguration
                    invoiceGroup={invoiceGroup}
                    refetchInvoiceGroup={refetchInvoiceGroup}
                    eInvoiceSenderExists={eInvoiceSenderExists}
                    onEInvoiceSenderStatusChange={(exists) => setEInvoiceSenderExists(exists)}
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={5}>
                  <ActivePaymentOptionsInfo
                    disabled={!isSuperAdmin}
                    eInvoiceSenderConnected={eInvoiceSenderExists}
                    electronicInvoiceEmailExists={!!invoiceGroup?.electronicInvoiceEmailNotification}
                  />
                  {isSuperAdmin && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button type='submit' variant='contained' disabled={saving}>
                        {tLocal('common:button.save')}
                      </Button>
                    </Box>
                  )}
                </Grid>
              </Grid>
            )}
          </form>
        </FormProvider>
      </Box>
    </>
  );
};
